import React from 'react';
import { TrustpilotStars } from './StarRating';
import { Phone, Mail, Globe, MapPin, MessageCircle, Clock } from 'lucide-react';

const CompanySidebar = ({ company }) => {
  if (!company) return null;

  const getRatingLabel = (score) => {
    if (score >= 4.5) return 'Excellent';
    if (score >= 4) return 'Très bien';
    if (score >= 3) return 'Bien';
    if (score >= 2) return 'Médiocre';
    return 'Mauvais';
  };

  const stats = company.stats || { trust_score: 0, total_reviews: 0, rating_distribution: {} };
  const ratingDistribution = stats.rating_distribution || {};

  return (
    <div className="space-y-6">
      {/* Trust Score Card */}
      <div className="bg-white rounded-lg border p-6">
        <div className="text-center mb-4">
          <p className="text-4xl font-bold text-[#1a1a1a] mb-1">{stats.trust_score}</p>
          <p className="text-lg font-semibold text-[#1a1a1a] mb-2">{getRatingLabel(stats.trust_score)}</p>
          <TrustpilotStars rating={stats.trust_score} size="md" />
          <p className="text-sm text-gray-500 mt-2">{stats.total_reviews} avis</p>
        </div>

        {/* Rating Distribution */}
        <div className="space-y-2 mt-6">
          {[5, 4, 3, 2, 1].map((stars) => {
            const percentage = ratingDistribution[String(stars)] || 0;
            return (
              <div key={stars} className="flex items-center gap-2">
                <span className="text-sm text-gray-600 w-16">{stars} étoile{stars > 1 ? 's' : ''}</span>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#00b67a] rounded-full transition-all duration-300"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="text-sm text-gray-500 w-10 text-right">{percentage}%</span>
              </div>
            );
          })}
        </div>

        {/* Separator */}
        <div className="border-t my-6" />

        {/* Response Stats */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <MessageCircle className="w-4 h-4 text-green-600" />
            <span className="text-gray-700">A répondu à <strong>{company.response_rate || 0}%</strong> de ses avis négatifs</span>
          </div>
          {company.response_time && (
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-gray-700">Répond généralement dans un délai de <strong>{company.response_time}</strong></span>
            </div>
          )}
        </div>
      </div>

      {/* Company Info Card */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-semibold text-[#1a1a1a] mb-4">À propos de l'entreprise</h3>
        
        <a href="#" className="text-sm text-[#1a1a1a] hover:underline block mb-4">
          {company.category}
        </a>

        <div className="border-t pt-4">
          <h4 className="font-semibold text-[#1a1a1a] mb-3">Coordonnées de contact</h4>
          
          <div className="space-y-3">
            {company.address && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{company.address}</span>
              </div>
            )}
            
            {company.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-gray-500" />
                <a href={`tel:${company.phone}`} className="text-[#1a1a1a] hover:underline">
                  {company.phone}
                </a>
              </div>
            )}
            
            {company.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-gray-500" />
                <a href={`mailto:${company.email}`} className="text-[#1a1a1a] hover:underline">
                  {company.email}
                </a>
              </div>
            )}
            
            {company.domain && (
              <div className="flex items-center gap-2 text-sm">
                <Globe className="w-4 h-4 text-gray-500" />
                <a
                  href={company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1a1a1a] hover:underline"
                >
                  {company.domain}
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transparency Link */}
      <a
        href="#"
        className="block text-sm text-gray-600 hover:text-[#1a1a1a] hover:underline"
      >
        Comment cette entreprise utilise Trustpilot →
      </a>
    </div>
  );
};

export default CompanySidebar;
